import { useRef, useState } from 'react';
import Button from '@/components/base/Button';
import { formatMoney } from '@/utils/format';
import type { Biller, FundingWallet } from '@/types/portal';

interface BillFormStepProps {
  favourites: Biller[];
  selectedBillerId: string | null;
  onSelectBiller: (biller: Biller) => void;
  onManageFavourites: () => void;
  paybill: string;
  onPaybill: (value: string) => void;
  account: string;
  onAccount: (value: string) => void;
  amount: number;
  onAmount: (value: number) => void;
  currency: string;
  wallets: FundingWallet[];
  walletId: string;
  onWallet: (id: string) => void;
  fee: number;
  total: number;
  error: string;
  canManage: boolean;
  canContinue: boolean;
  onContinue: () => void;
}

/**
 * "Pay a Bill" form — pick a favourite biller (or type a paybill), then capture
 * the account number and amount. Mirrors the mobile paybill UX on desktop.
 */
export default function BillFormStep({
  favourites,
  selectedBillerId,
  onSelectBiller,
  onManageFavourites,
  paybill,
  onPaybill,
  account,
  onAccount,
  amount,
  onAmount,
  currency,
  wallets,
  walletId,
  onWallet,
  fee,
  total,
  error,
  canManage,
  canContinue,
  onContinue,
}: BillFormStepProps) {
  const paybillRef = useRef<HTMLInputElement>(null);
  const [scanHint, setScanHint] = useState(false);
  const money = currency || 'KES';
  const wallet = wallets.find((item) => item.id === walletId) ?? null;

  return (
    <div className="space-y-7">
      {/* Favourites */}
      <section>
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-base font-semibold text-foreground-950">Favourites</h3>
          <button
            type="button"
            onClick={onManageFavourites}
            className="cursor-pointer text-xs font-label font-medium text-primary-700 transition-colors hover:text-primary-800"
          >
            Manage
          </button>
        </div>

        <ul className="mt-4 flex flex-wrap gap-x-6 gap-y-4">
          <li>
            <button
              type="button"
              onClick={onManageFavourites}
              className="flex w-[72px] cursor-pointer flex-col items-center gap-2 text-center"
            >
              <span className="flex h-14 w-14 items-center justify-center rounded-full border border-dashed border-background-300 bg-background-100 text-xl text-foreground-500 transition-colors hover:border-primary-300 hover:text-primary-700">
                <i className="ri-add-line" />
              </span>
              <span className="text-xs text-foreground-500">Add</span>
            </button>
          </li>
          {favourites.map((biller) => {
            const isActive = biller.id === selectedBillerId;
            return (
              <li key={biller.id}>
                <button
                  type="button"
                  onClick={() => onSelectBiller(biller)}
                  aria-pressed={isActive}
                  className="flex w-[72px] cursor-pointer flex-col items-center gap-2 text-center"
                >
                  <span
                    className={`flex h-14 w-14 items-center justify-center rounded-full text-base font-semibold transition-colors ${
                      isActive
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-accent-100 text-accent-800'
                    }`}
                  >
                    {biller.mark}
                  </span>
                  <span
                    className={`line-clamp-2 text-xs ${
                      isActive ? 'font-medium text-foreground-950' : 'text-foreground-600'
                    }`}
                  >
                    {biller.name}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </section>

      {/* Paybill + account + amount */}
      <section className="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label htmlFor="bill-paybill" className="text-sm font-label text-foreground-700">
            Enter paybill
          </label>
          <div className="relative mt-2">
            <input
              id="bill-paybill"
              ref={paybillRef}
              value={paybill}
              inputMode="numeric"
              onChange={(event) => onPaybill(event.target.value.replace(/\D/g, ''))}
              placeholder="Enter paybill"
              className="h-12 w-full rounded-lg border border-background-300 bg-background-50 pl-4 pr-24 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
            />
            <div className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1">
              <button
                type="button"
                aria-label="Find a biller"
                onClick={onManageFavourites}
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-lg text-primary-600 transition-colors hover:bg-primary-50"
              >
                <i className="ri-search-line" />
              </button>
              <span className="h-6 w-px bg-background-300" aria-hidden="true" />
              <button
                type="button"
                aria-label="Scan paybill QR code"
                onClick={() => setScanHint((value) => !value)}
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-lg text-foreground-600 transition-colors hover:bg-background-200"
              >
                <i className="ri-qr-scan-2-line" />
              </button>
            </div>
          </div>
          {scanHint ? (
            <p className="mt-1.5 flex items-center gap-1.5 text-xs text-foreground-500">
              <i className="ri-information-line" />
              Scan the QR code on your bill to autofill the paybill and account number.
            </p>
          ) : null}
        </div>

        <div>
          <label htmlFor="bill-account" className="text-sm font-label text-foreground-700">
            Enter account number
          </label>
          <input
            id="bill-account"
            value={account}
            onChange={(event) => onAccount(event.target.value.replace(/[^0-9a-zA-Z]/g, ''))}
            placeholder="Enter account number"
            className="mt-2 h-12 w-full rounded-lg border border-background-300 bg-background-50 px-4 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
          />
        </div>

        <div>
          <label htmlFor="bill-amount" className="text-sm font-label text-foreground-700">
            Enter amount
          </label>
          <div className="relative mt-2">
            <input
              id="bill-amount"
              type="number"
              min={0}
              value={Number.isNaN(amount) || amount === 0 ? '' : amount}
              onChange={(event) => onAmount(Number(event.target.value))}
              placeholder="0"
              className="h-12 w-full rounded-lg border border-background-300 bg-background-50 pl-4 pr-16 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
            />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-foreground-500">
              {money === 'KES' ? 'Ksh' : money}
            </span>
          </div>
        </div>

        <div className="sm:col-span-2">
          <label htmlFor="bill-from" className="text-sm font-label text-foreground-700">
            Send from
          </label>
          <select
            id="bill-from"
            value={walletId}
            onChange={(event) => onWallet(event.target.value)}
            className="mt-2 h-12 w-full cursor-pointer rounded-lg border border-background-300 bg-background-50 px-4 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
          >
            <option value="">Select a wallet...</option>
            {wallets.map((item) => (
              <option key={item.id} value={item.id}>
                {item.currency} float · {formatMoney(item.balance, item.currency)}
              </option>
            ))}
          </select>
        </div>
      </section>

      {/* Balance strip */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1 rounded-lg border border-background-200 bg-background-100 px-4 py-3 text-xs text-foreground-500">
        <span>
          Balance:{' '}
          <span className="font-medium text-foreground-800">
            {wallet ? formatMoney(wallet.balance, wallet.currency) : '—'}
          </span>
        </span>
        <span>
          Transaction cost:{' '}
          <span className="font-medium text-foreground-800">{fee > 0 ? formatMoney(fee, money) : 'N/A'}</span>
        </span>
        <span>
          Total:{' '}
          <span className="font-medium text-foreground-950">{formatMoney(total, money)}</span>
        </span>
      </div>

      {!canManage ? (
        <p className="flex items-center gap-1.5 text-xs text-primary-700">
          <i className="ri-lock-line" />
          Your role can view payments but not send them.
        </p>
      ) : error ? (
        <p className="flex items-center gap-1.5 text-xs text-primary-700">
          <i className="ri-error-warning-line" />
          {error}
        </p>
      ) : null}

      <Button
        icon="ri-arrow-right-line"
        size="lg"
        onClick={onContinue}
        disabled={!canContinue}
        className="w-full sm:w-auto sm:min-w-[220px]"
      >
        Review payment
      </Button>
    </div>
  );
}